/* Forgot-password page — collects an email, hits POST /api/auth/forgot-password,
   and always shows a success confirmation (backend never confirms whether an
   email exists, so neither can the UI). */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { extractErrorMessage } from "@/lib/apiError";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  teal: "#0A8B85",
  tealHover: "#066667",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const ClinqLogo = ({ withTagline = true }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo-forgot">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    {withTagline && (
      <span className="flex flex-col leading-none text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "rgba(255,255,255,0.55)" }}>
        <span>Funder</span>
        <span className="mt-0.5">Readiness</span>
      </span>
    )}
  </div>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend deliberately returns 200 even on errors to prevent
      // email enumeration — a rejection here means the request never
      // reached the server. Fall back to the same confirmation UI so we
      // stay silent on validity.
      toast.error(extractErrorMessage(err, "Could not send reset link. Please try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-white"
      style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }}
      data-testid="forgot-password-page">
      <SEO title="Reset your password" path="/forgot-password" noindex />
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><ClinqLogo /></Link>
          <Link to="/login" data-testid="header-login"
            className="text-sm font-medium text-white/90 hover:text-white px-3 py-2">
            Log In
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/50">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
              Reset your password
            </div>
            <h1 className="font-extrabold text-4xl mt-3 tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              Forgot your <span style={{ color: C.teal }}>password?</span>
            </h1>
            <p className="mt-2 text-sm text-white/60">
              We&apos;ll email you a link to set a new one.
            </p>
          </div>

          <div className="rounded-2xl p-7 border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
            {sent ? (
              <div data-testid="forgot-password-sent" className="text-sm text-white/80 space-y-3">
                <div className="text-[12px] uppercase tracking-widest font-bold" style={{ color: C.orange }}>
                  Check your inbox
                </div>
                <p>
                  If <strong className="text-white">{email}</strong> matches a Clinq account,
                  we just emailed a link to reset your password.
                </p>
                <p className="text-white/50 text-[13px]">
                  The link expires in <strong style={{ color: C.tealSoft }}>1 hour</strong>. Didn&apos;t get it?
                  Check spam, or {" "}
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="forgot-password-retry"
                    className="underline hover:text-white"
                  >try a different email</button>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="forgot-password-email-input"
                    placeholder="founder@yourco.com"
                    className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                    style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-password-submit"
                  className="w-full py-2.5 rounded-md font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: busy ? C.tealHover : C.teal }}
                >
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-white/60">
            Remembered it?{" "}
            <Link to="/login" data-testid="forgot-to-login" className="font-semibold hover:underline" style={{ color: C.tealSoft }}>
              Back to log in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
